<script lang="ts" setup>
import { computed, onMounted, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import { RegistrationChannel } from "@/shared/@types/RegistrationChannel";
import Button from "@/shared/components/Button/Button.vue";
import Form from "@/shared/components/Form/Form.vue";
import Infobox from "@/shared/components/Infobox/Infobox.vue";
import TextField from "@/shared/components/TextField/TextField.vue";
import type { InitialRegistrationData } from "@/app/registration-app/user/shared/@types/InitialRegistrationData";
import type { PreRegistrationValidationData } from "@/app/registration-app/user/shared/@types/PreRegistrationValidationData";
import { usePreRegistrationValidationFields } from "./usePreRegistrationValidationFields.composable";

const { t } = useI18n();

const props = defineProps<{
  channel: RegistrationChannel;
  initialData?: InitialRegistrationData;
  errorMessage?: string;
  isLoading?: boolean;
  showDocumentIdField?: boolean;
}>();

const { channel, initialData, showDocumentIdField } = toRefs(props);

const emit = defineEmits<{
  (e: "validate-data", fields: PreRegistrationValidationData): void;
}>();

const {
  customerId: { value: customerId, errors: customerIdErrors },
  documentId: { value: documentId, errors: documentIdErrors },
  email: { value: email, errors: emailErrors },
  handleSubmit,
  resetForm,
} = usePreRegistrationValidationFields(initialData.value, showDocumentIdField);

const onSubmit = handleSubmit(() =>
  emit("validate-data", {
    customerId: customerId.value,
    documentId: showDocumentIdField.value ? documentId.value : "",
    email: email.value,
    channel: channel.value,
  }),
);

const documentIdLabel = computed(() =>
  channel.value === RegistrationChannel.Fbs
    ? t("shared.term.billing.number")
    : t("registration.user.gasShop.form.documentId"),
);

onMounted(() => resetForm());
</script>

<template>
  <Form
    novalidate
    @submit="onSubmit"
  >
    <template #default>
      <TextField
        v-model="customerId"
        :errors="customerIdErrors"
        :label="$t('shared.term.customerNumber.default')"
        :placeholder="$t('shared.term.customerNumber.default')"
        required
      />
      <TextField
        v-model="email"
        :errors="emailErrors"
        :label="$t('shared.term.email.default')"
        :placeholder="$t('shared.term.email.default')"
        autocomplete="email"
        required
      />
      <template v-if="showDocumentIdField">
        <Infobox status="success">
          {{ $t("registration.shared.preRegistrationVerified") }}
        </Infobox>
        <TextField
          v-model="documentId"
          :errors="documentIdErrors"
          :label="documentIdLabel"
          :placeholder="$t('shared.form.field.documentId.placeholder')"
          required
        />
      </template>
      <Infobox
        v-if="errorMessage"
        status="error"
      >
        {{ errorMessage }}
      </Infobox>
    </template>
    <template #actions>
      <Button
        type="submit"
        :caption="
          showDocumentIdField
            ? $t('shared.action.next')
            : $t('registration.shared.action.verify')
        "
        :is-loading="isLoading"
      />
    </template>
  </Form>
</template>
